import jsPDF from 'jspdf';
import moment from 'moment';
import React from 'react';
import 'jspdf-autotable';
import styled from 'styled-components';
import { PrintButton } from 'components/PrintButton';
import { KartPassLogo } from 'pages/app/Transactions/TransactionsPrintList/kartpass-logo';
import { Logo } from 'pages/app/Transactions/TransactionsPrintList/logo';
import { TicketHossLogo } from 'pages/app/Transactions/TransactionsPrintList/tickethoss-logo';
import 'moment-timezone';

// PDF export for the gen-3 Ticket Breakdown report. Mirrors the screen: three
// quantities-only sections (Ticket Breakdown, Tickets by Day, Tier Breakdown)
// with date-group header rows inside the tables. No dollar amounts.

// Ticket Hoss sells tickets; every other platform sells passes.
const IS_TICKETHOSS = 'tickethoss' === process.env.REACT_APP_PLATFORM;
const UNIT = IS_TICKETHOSS ? 'Ticket' : 'Pass';
const UNITS = IS_TICKETHOSS ? 'Tickets' : 'Passes';

const ButtonContainer = styled.div`
  & > button {
    width: 100%;

    @media screen and (min-width: 861px) {
      width: auto;
    }
  }
`;

const ORANGE = '#fa4616';
const HEAD_FILL = [22, 22, 43];
const GROUP_FILL = [243, 245, 248];

const groupHeaderRow = (label, total) => [
  {
    content: label.toUpperCase(),
    styles: { fillColor: GROUP_FILL, textColor: ORANGE, fontStyle: 'bold' },
  },
  {
    content: null == total ? '' : `${total}`,
    styles: {
      fillColor: GROUP_FILL,
      textColor: '#000',
      fontStyle: 'bold',
      halign: 'right',
    },
  },
];

const qtyCell = (qty, styles = {}) => ({
  content: `${qty}`,
  styles: { halign: 'right', ...styles },
});

const buildGroupedBody = (groups, withPrice) => {
  const body = [];
  (groups || []).forEach((group) => {
    const header = groupHeaderRow(group.label, group.total);
    if (withPrice) {
      header.splice(1, 0, { content: '', styles: { fillColor: GROUP_FILL } });
    }
    body.push(header);
    (group.tickets || []).forEach((ticket) => {
      body.push(
        withPrice
          ? [
              ticket.name,
              { content: ticket.price || '', styles: { halign: 'right' } },
              qtyCell(ticket.qty),
            ]
          : [ticket.name, qtyCell(ticket.qty)]
      );
    });
  });
  return body;
};

const buildTierBody = (tierBreakdown) => {
  const body = [];
  (tierBreakdown || []).forEach((ticket) => {
    body.push([
      { content: ticket.name, styles: { fontStyle: 'bold' } },
      { content: '' },
      qtyCell(ticket.qty, { fontStyle: 'bold' }),
    ]);
    (ticket.tiers || []).forEach((tier) => {
      body.push([
        {
          content: `      ${tier.name}`,
          styles: { textColor: [100, 116, 139] },
        },
        {
          content: tier.price || '',
          styles: { halign: 'right', textColor: [100, 116, 139] },
        },
        qtyCell(tier.qty, { textColor: [100, 116, 139] }),
      ]);
    });
  });
  return body;
};

export const TicketBreakdownReportPrint = ({
  date,
  report,
  text,
  textStyle,
  button,
}) => {
  const isKartPass = 'kartpass' === process.env.REACT_APP_PLATFORM;
  const isTicketHoss = 'tickethoss' === process.env.REACT_APP_PLATFORM;

  const exportPDF = async () => {
    const event = report.event;
    const unit = 'pt';
    const size = 'A4';
    const orientation = 'portrait';

    const doc = new jsPDF(orientation, unit, size, true);
    const pageSize = doc.internal.pageSize;
    const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
    const pageHeight = pageSize.height ?? pageSize.getHeight();

    doc.setFontSize(15);
    doc.setFontStyle('bold');

    doc.text(String(date || ''), 40, 40);
    doc.text(
      event?.name.length > 60 ? event?.name.slice(0, 60) : event?.name,
      40,
      60
    );
    let Y = 60;
    if (event?.name.length > 60) {
      Y += 20;
      doc.text(event?.name.slice(60, 120), 40, Y);
    }

    if (event?.track?.name) {
      Y += 20;
      doc.text(event.track.name, 40, Y);
    }

    Y += 20;
    doc.text(`${UNITS} Sold - ${report.tickets_sold}`, 40, Y);

    doc.text(`${UNIT} Breakdown Report`, pageWidth - 40, 100, {
      align: 'right',
    });

    if (isKartPass) {
      doc.addImage(
        KartPassLogo,
        'PNG',
        pageWidth - 140,
        20,
        100,
        75,
        '',
        'FAST'
      );
    } else if (isTicketHoss) {
      doc.addImage(
        TicketHossLogo,
        'PNG',
        pageWidth - 140,
        25,
        100,
        54,
        '',
        'FAST'
      );
    } else {
      doc.addImage(Logo, 'PNG', pageWidth - 115, 20, 75, 75, '', 'FAST');
    }

    Y = 130;

    const sections = [
      {
        title: `Total ${UNIT} Breakdown`,
        head: [[UNIT, 'Price', 'Qty']],
        withPrice: true,
        body: buildGroupedBody(report.ticket_groups, true),
        foot: [
          [
            { content: 'TOTAL', styles: { fontStyle: 'bold' } },
            { content: '' },
            qtyCell(report.tickets_sold, { fontStyle: 'bold' }),
          ],
        ],
      },
      {
        title: `${UNITS} by Day`,
        head: [[UNIT, 'Qty']],
        body: buildGroupedBody(report.days),
      },
      {
        title: 'Tier Breakdown',
        head: [[`${UNIT} / Tier`, 'Price', 'Qty']],
        withPrice: true,
        body: buildTierBody(report.tier_breakdown),
      },
    ].filter((section) => section.body.length > 0);

    sections.forEach((section) => {
      if (Y > pageHeight - 120) {
        doc.addPage();
        Y = 40;
      }

      doc.setFontSize(13);
      doc.setFontStyle('bold');
      doc.setTextColor(ORANGE);
      doc.text(section.title, 40, Y);
      doc.setTextColor('#000');
      Y += 10;

      doc.autoTable({
        startY: Y,
        rowPageBreak: 'avoid',
        head: section.head,
        body: section.body,
        foot: section.foot,
        styles: {
          fontStyle: 'normal',
        },
        columnStyles: section.withPrice
          ? {
              1: { halign: 'right', cellWidth: 90 },
              2: { halign: 'right', cellWidth: 70 },
            }
          : {
              1: { halign: 'right', cellWidth: 70 },
            },
        headStyles: {
          fillColor: isKartPass ? '#00001F' : HEAD_FILL,
        },
        footStyles: {
          fillColor: GROUP_FILL,
          textColor: '#000',
        },
        tableWidth: 'auto',
      });

      Y = doc.lastAutoTable.finalY + 30;
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setFontStyle('normal');

      const now = moment();
      const momentInSpecificTimezone = now.tz('America/New_York');
      const dateTimeStamp =
        momentInSpecificTimezone.format('MMM DD - YYYY h:mm A').toUpperCase() +
        ' EST';
      doc.text(`Exported ${dateTimeStamp}`, pageWidth - 40, pageHeight - 20, {
        align: 'right',
      });
    }

    doc.setProperties({
      title: `${date} - ${event.name} - ${UNIT} Breakdown Report.pdf`,
      subject: `${date} - ${event.name} - ${UNIT} Breakdown Report`,
      author: process.env.REACT_APP_PLATFORM,
    });
    return {
      ...doc,
      fileName: `${date} - ${event.name} - ${UNIT} Breakdown Report.pdf`,
    };
  };

  return (
    <ButtonContainer>
      <PrintButton
        buildPdf={exportPDF}
        text={text}
        disabled={!report}
        variant={'minimal'}
        textStyle={textStyle}
        button={button}
      />
    </ButtonContainer>
  );
};
